import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from "react-query";
import { useNavigate, useSearchParams } from 'react-router-dom';
// import cn from 'classnames';
import useTab from "@hooks/useTab";
import { Text } from "@components/new/Typo";
import Page from '@components/Page';
import { Alert, AlertType } from "@components/new/Alert";
import MenuItem from '@components/new/MenuItem';
import IconCountry from '@components/new/IconCountry';
import { NoData } from "@components/new/NoData";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import IconPlus from '@assets/icons/outline/plus.svg?react';
import IconMap from '@assets/icons/outline/map-pin.svg?react';
import Skeleton from "@components/new/Skeleton";
import { WarehouseSelect } from "./components/WarehouseSelect";
import { TabsList, TabsContent } from "@components/new/Tabs/Tabs";
import { getSellers } from '@api/apiClient/sellers';
import AvailabilityTab from "./tabs/AvailabilityTab";
import MatchingTab from "./tabs/MatchingTab";
import ProblematicTab from "./tabs/ProblematicTab";
import OrdersTab from "./tabs/OrdersTab";
import { Tooltip } from '@components/new/Tooltip';
import {
  getWarehouses,
  useFBPRequestsCounter,
  useFBPStocksCounter,
  useFBPMatchingCounter,
  useFBPProblematicRequestsCounter,
  useFBPWithdrawalRequestsCounter,
  TWarehouse,
  TWarehouses,
  TWbWarehouse,
  TSellerWarehouse,
} from '@api/apiClient/fbp';
import AddWarehouseModal from "./modals/AddWarehouseModal";
import CreateOrderModal from "./modals/CreateOrderModal";
import WarehouseInfoModal from "./modals/WarehouseInfoModal";
import PickUpModal from "./modals/PickUpModal";
import { useKnowledgeBase } from '@contexts/KnowledgeBaseContext';
import styles from './FBPPage.module.scss';

const FBPPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [wbWarehouseWithExpiringKey, setWbWarehouseWithExpiringKey] = useState<TWbWarehouse[]>([]);
  // Магазины с недействующим ключом: WB (Invalid/Expired) и Ali (Invalid)
  const [expiredKeyStores, setExpiredKeyStores] = useState<{ sellerId: string; sellerName: string }[]>([]);
  const [activeWarehouse, setActiveWarehouse] = useState<TWarehouse | undefined>();
  const [activeSellerWarehouse, setActiveSellerWarehouse] = useState<TSellerWarehouse | undefined>();
  const [warehouseForEdit, setWarehouseForEdit] = useState<TSellerWarehouse | null>(null);
  const [isOpenAddSWarehouseModal, setOpenAddSWarehouseModal] = useState<boolean>(false);
  const [isOpenCreateOrderModal, setOpenCreateOrderModal] = useState<boolean>(false);
  const [isOpenWarehouseInfo, setOpenWarehouseInfo] = useState<boolean>(false);
  const [isPickUpModalOpen, setPickUpModalOpen] = useState<boolean>(false);
  const [pickUpId, setPickUpId] = useState<string>("");
  const [warehouseInfo, setWarehouseInfo] = useState<TWarehouse | undefined>(undefined);
  const { data: requestsCounter } = useFBPRequestsCounter(activeSellerWarehouse?.id ?? "");
  const { data: stocksCounter } = useFBPStocksCounter(activeSellerWarehouse?.id ?? "");
  const { data: withdrawalCounter } = useFBPWithdrawalRequestsCounter(activeSellerWarehouse?.id ?? "");
  const { data: matchingCounter } = useFBPMatchingCounter(activeSellerWarehouse?.id ?? "");
  const { data: problematicCounter } = useFBPProblematicRequestsCounter(activeSellerWarehouse?.id ?? "");
  const { setKnowledgeBaseParams } = useKnowledgeBase();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultSellerWarehouseId = searchParams.get("sellerWarehouseId");
  const defaultWarehouseId = searchParams.get("warehouseId");

  const updateSearchParams = (data: Record<string, string | undefined>) => {
    const allParams = { ...Object.fromEntries(searchParams.entries()) };
    setSearchParams({
      ...(Object.keys(allParams).reduce((acc, k) => ({ ...acc, [k]: allParams[k] }), {})),
      ...data,
    });
  }

  const warehousesQuery = useQuery({
    queryKey: ["getWarehouses"],
    queryFn: () => getWarehouses(),
    retry: 1,
    onSuccess: (data: TWarehouses) => {
      const newData = (activeWarehouse
        && data?.fbpWarehouses.find(v => (v.id === activeWarehouse.id))
      ) || (
        defaultWarehouseId
          && data?.fbpWarehouses.find(v => (v.id === defaultWarehouseId))
      ) || data?.fbpWarehouses?.[0];
      setActiveWarehouse(newData);
      updateSearchParams({ warehouseId: newData?.id ?? undefined, })
    }
  });

  const { data: sellersData } = useQuery({
    queryKey: ["sellers"],
    queryFn: () => getSellers(),
    retry: 1,
  });

  const tabs = [
    {
      id: "Availability",
      title: t('fbpPage.tabs.Availability'),
      description: t('fbpPage.tabs.AvailabilityDescription'),
      content: (
        <AvailabilityTab
          sellerWarehouse={activeSellerWarehouse}
          openCreateOrderModal={() => setOpenCreateOrderModal(true)}
        />
      ),
      count: stocksCounter ?? 0,
    },
    {
      id: "Applications",
      title: t('fbpPage.tabs.Applications'),
      // description: t('fbpPage.tabs.ApplicationsDescription'),
      content: (
        <OrdersTab
          sellerWarehouse={activeSellerWarehouse}
          activeWarehouse={activeWarehouse}
          openCreateOrderModal={() => setOpenCreateOrderModal(true)}
        />
      ),
      count: (requestsCounter ?? 0) + (withdrawalCounter ?? 0),
    },
    // {
    //   id: "Discrepancies",
    //   title: t('fbpPage.tabs.Discrepancies'),
    //   content: (
    //     <div>Tab3 content</div>
    //   ),
    //   disable: true,
    //   // count: 999
    // },
    {
      id: "Matching",
      title: t('fbpPage.tabs.Matching'),
      content: (
        <MatchingTab
          sellerWarehouseId={activeSellerWarehouse?.id ?? ""}
          hasAliStores={Boolean(activeSellerWarehouse?.aliWarehouses?.length)}
          hasWbStores={Boolean(activeSellerWarehouse?.wbWarehouses?.length)}
          hasYmStores={Boolean(activeSellerWarehouse?.ymWarehouses?.length)}
        />
      ),
      count: matchingCounter ?? 0,
    },
    {
      id: "Problematic",
      title: t('fbpPage.tabs.Problematic'),
      content: (
        <ProblematicTab
          sellerWarehouseId={activeSellerWarehouse?.id ?? ""}
          warehouseAddress={activeWarehouse?.address ?? ""}
          warehousePhone={activeWarehouse?.contactPhone ?? ""}
        />
      ),
      count: problematicCounter ?? 0,
    },
  ]

  const { activeTab, setActiveTab } = useTab(tabs, undefined, ["sellerWarehouseId"]);
  const sellerWarehouses = activeWarehouse?.sellerWarehouses ?? [];
  const noWarehouses = !sellerWarehouses?.length;
  const handleSellerWarehouseSelection = (id: string) => {
    setActiveSellerWarehouse(sellerWarehouses.find(v => (v.id === id)));
    updateSearchParams({ sellerWarehouseId: id, })
  }

  useEffect(() => {
    if (sellerWarehouses?.length &&
      (!activeSellerWarehouse
        || !sellerWarehouses.find(v => (v.id === activeSellerWarehouse?.id))
      )
    ) {
      if (defaultSellerWarehouseId) {
        setActiveSellerWarehouse(sellerWarehouses.find(v => v.id === defaultSellerWarehouseId) ?? sellerWarehouses[0]);
      } else {
        setActiveSellerWarehouse(sellerWarehouses[0]);
      }
    }
  }, [sellerWarehouses])

  const editWarehouse = (item: TSellerWarehouse | null) => {
    setOpenAddSWarehouseModal(true);
    setWarehouseForEdit(item);
  };

  useEffect(() => {
    const wbWarehouses = activeSellerWarehouse?.wbWarehouses || [];
    const aliWarehouses = activeSellerWarehouse?.aliWarehouses || [];
    setWbWarehouseWithExpiringKey(
      wbWarehouses.filter(v => v.wbSeller?.tokenStatus === "Expiring")
    );
    setExpiredKeyStores([
      ...wbWarehouses
        .filter(v => v.wbSeller?.tokenStatus === "Invalid" || v.wbSeller?.tokenStatus === "Expired")
        .map(v => ({ sellerId: v.sellerId, sellerName: v.sellerName })),
      ...aliWarehouses
        .filter(v => v.tokenStatus === "Invalid")
        .map(v => ({ sellerId: v.sellerId, sellerName: v.name })),
    ]);
  }, [activeSellerWarehouse])

  return (
    <Page
      title={t('fbpPage.title')}
      description={t('fbpPage.description')}
    >
      <div className={styles.pageContent}>
        <div className={styles.warehouses}>
          {warehousesQuery.isLoading ? (
            <>
              <Skeleton height="34px" />
              <Skeleton height="34px" />
              <Skeleton height="34px" />
            </>
          ) : (
            (warehousesQuery.data?.fbpWarehouses ?? []).map((w: TWarehouse) => (
              <MenuItem
                key={w.id}
                title={<Text as="div" width={160} ellipsis lines={1}>{w.name}</Text>}
                active={w.id === activeWarehouse?.id}
                onClick={() => {
                  setActiveWarehouse(w);
                  updateSearchParams({ warehouseId: w?.id ?? undefined, })
                }}
                leftDecoration={<IconCountry countryCode={w?.countryCode ?? ""} />}
                rightDecoration={(
                  <IconButton
                    className={styles.addressBtn}
                    icon={IconMap}
                    type={IconButtonType.ICON}
                    size={IconButtonSize.M}
                    onClick={() => {
                      setWarehouseInfo(w);
                      setOpenWarehouseInfo(true);
                    }}
                  />
                )}
              />
            ))
          )}
        </div>

        <div className={styles.vLine} />

        <div className={styles.main}>
          {(!sellersData?.items?.length) ? (
            <NoData
              title={t("fbpPage.noSellersTitle")}
              description={t("fbpPage.noSellersDescription")}
              btnTitle={t('fbpPage.noSellersBtn')}
              onClick={() => navigate('/stores')}
            />
          ) : (
            <>
              {noWarehouses ? null : (
                <>
                  <div className={styles.selectGroup}>
                    <WarehouseSelect
                      value={activeSellerWarehouse?.id}
                      onChange={handleSellerWarehouseSelection}
                      items={sellerWarehouses}
                      activeWarehouse={activeWarehouse}
                      editWarehouse={editWarehouse}
                    />
                    <Tooltip
                      action={
                        <IconButton
                          icon={IconPlus}
                          type={IconButtonType.SECONDARY}
                          size={IconButtonSize.M}
                          onClick={() => setOpenAddSWarehouseModal(true)}
                          disabled={!(activeWarehouse?.fbpAvailableMarketplaces.wildberries || activeWarehouse?.fbpAvailableMarketplaces.yandex || activeWarehouse?.fbpAvailableMarketplaces.aliexpress)}
                        />
                      }
                      text={(t("fbpPage.addWarehouseTooltip"))}
                      position="top"
                      align="start"
                    />
                  </div>
                  {wbWarehouseWithExpiringKey?.length ? (
                    expiredKeyStores?.length ? (
                      <Alert
                        type={AlertType.ERROR}
                        title={t("fbpPage.apiKeyMultipleTitle")}
                        description={t("fbpPage.apiKeyMultipleDescription")}
                        button={{
                          type: "link",
                          title: t("fbpPage.updateBtn"),
                          onClick: () => navigate(`/stores`),
                        }}
                      />
                    ) : (
                      <Alert
                        type={AlertType.WARNING}
                        title={t(wbWarehouseWithExpiringKey?.length > 1 ? "fbpPage.apiKeyExpiringMultipleTitle" : "fbpPage.apiKeyExpiringTitle", { store: wbWarehouseWithExpiringKey[0]?.sellerName ?? "", count: wbWarehouseWithExpiringKey?.length })}
                        description={wbWarehouseWithExpiringKey?.length > 1 ? t("fbpPage.apiKeyExpiringMultipleDescription") : t("fbpPage.apiKeyExpiringDescription")}
                        button={{
                          type: "link",
                          title: t("fbpPage.updateBtn"),
                          onClick: () => navigate(`/stores?storeId=${wbWarehouseWithExpiringKey[0]?.sellerId}`),
                        }}
                      />
                    )
                  ) : (
                    expiredKeyStores?.length ? (
                      <Alert
                        type={AlertType.ERROR}
                        title={t(expiredKeyStores?.length > 1 ? "fbpPage.apiKeyExpiredMultipleTitle" : "fbpPage.apiKeyExpiredTitle", { store: expiredKeyStores[0]?.sellerName ?? "", count: expiredKeyStores?.length })}
                        description={expiredKeyStores?.length > 1 ? t("fbpPage.apiKeyExpiredMultipleDescription") : t("fbpPage.apiKeyExpiredDescription")}
                        button={{
                          type: "link",
                          title: t("fbpPage.updateBtn"),
                          onClick: () => navigate(`/stores?storeId=${expiredKeyStores[0]?.sellerId}`),
                        }}
                      />
                    ) : null
                  )}
                </>
              )}
              <div className={styles.tabsRow}>
                {noWarehouses ? null : (
                  <TabsList
                    tabs={tabs}
                    onChange={setActiveTab}
                    value={activeTab}
                  />
                )}
              </div>
              {(noWarehouses) ? (
                <NoData
                  title={t("fbpPage.noStoresTitle")}
                  description={t("fbpPage.noStoresDescription")}
                  btnTitle={t('fbpPage.noStoresBtn')}
                  onClick={() => setOpenAddSWarehouseModal(true)}
                  btnDisabled={!(activeWarehouse?.fbpAvailableMarketplaces.wildberries || activeWarehouse?.fbpAvailableMarketplaces.yandex || activeWarehouse?.fbpAvailableMarketplaces.aliexpress)}
                  footer={(
                    <LinkButton
                      size={LinkButtonSize.L}
                      title={t('fbpPage.instructions')}
                      onClick={() => setKnowledgeBaseParams(`https://unitrade-global.com/page139704916.html`)}
                    />
                  )}
                />
              ) : (
                <TabsContent
                  tab={tabs.find((tab) => (tab.id === activeTab))}
                />
              )}
            </>
          )}
        </div>
      </div>

      {(activeWarehouse && isOpenAddSWarehouseModal) ? (
        <AddWarehouseModal
          onClose={() => {
            setOpenAddSWarehouseModal(false);
            setWarehouseForEdit(null);
          }}
          sellerWarehouse={warehouseForEdit!}
          warehouse={activeWarehouse}
        />
      ) : null}

      {isOpenCreateOrderModal ? (
        <CreateOrderModal
          onClose={() => setOpenCreateOrderModal(false)}
          sellerWarehouseId={activeSellerWarehouse?.id ?? ""}
          fbpWarehouseId={activeWarehouse?.id ?? ""}
          isPickUpAvailable={activeWarehouse?.isPickUpAvailable ?? false}
          wbDimensionLimits={activeWarehouse?.wbSettings?.productDimensionLimits}
          aliDimensionLimits={activeWarehouse?.aliSettings?.productDimensionLimits}
          ymDimensionLimits={activeWarehouse?.ymSettings?.productDimensionLimits}
          wbDensityLimits={activeWarehouse?.wbSettings?.productDensitySettings?.minKgM3}
          aliDensityLimits={activeWarehouse?.aliSettings?.productDensitySettings?.minKgM3}
          ymDensityLimits={activeWarehouse?.ymSettings?.productDensitySettings?.minKgM3}
          openPickUp={(id: string) => {
            setPickUpModalOpen(true);
            setPickUpId(id);
          }}
        />
      ) : null}

      {isOpenWarehouseInfo ? (
        <WarehouseInfoModal
          onClose={() => setOpenWarehouseInfo(false)}
          warehouseInfo={warehouseInfo}
        />
      ) : null}

      {isPickUpModalOpen && (
        <PickUpModal
          onClose={() => setPickUpModalOpen(false)}
          // sellerWarehouseId={sellerWarehouseId}
          restockRequestId={pickUpId}
          fbpWarehouseId={activeWarehouse?.id ?? ""}
        />
      )}
    </Page>
  );
};

export default FBPPage;
